<template>
  <v-dialog v-model="isOpen" max-width="760" style="z-index: 1999">
    <template #activator="{ props: activatorProps }">
      <slot name="activator" :props="activatorProps" :disabled="disabled">
        <v-btn v-bind="activatorProps" :disabled="disabled">
          <v-icon class="mr-2" icon="mdi-plus"></v-icon>
          <span>Додати резолюцію</span>
        </v-btn>
      </slot>
    </template>

    <dialog-template
      :disabled="disabled || $loading.isLoading('documentResolutionCreate')"
      dialog-width="760px"
      submit-label="Створити"
      subtitle="Заповніть обов'язкові поля для створення завдання"
      title="Нова резолюція"
      @close="isOpen = false"
      @submit="submit">
      <div class="d-flex flex-column ga-3">
        <div>
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="d-flex align-center ga-2">
              <v-icon icon="mdi-format-text" size="x-small"></v-icon>
              <span class="label mb-0">Назва *</span>
            </div>
            <span class="text-caption text-disabled">
              {{ textLength }}/128
            </span>
          </div>
          <b-input
            v-model="resolution.text"
            :error-messages="getErrorMessages(v$.text)"
            counter="128"
            hide-details
            maxlength="128"
            placeholder="Наприклад: Підготувати квартальний звіт"
            @blur="v$.text.$touch()"></b-input>
        </div>

        <div>
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="d-flex align-center ga-2">
              <v-icon icon="mdi-format-align-left" size="x-small"></v-icon>
              <span class="label mb-0">Опис</span>
            </div>
            <span class="text-caption text-disabled">
              {{ commentLength }} символів
            </span>
          </div>
          <b-textarea
            v-model="resolution.comment"
            hide-details
            placeholder="Опишіть деталі: що потрібно зробити, які матеріали використати, який очікуваний результат…"
            rows="3"></b-textarea>
        </div>

        <div>
          <div class="d-flex align-center ga-2 mb-2">
            <v-icon icon="mdi-account-outline" size="x-small"></v-icon>
            <span class="label mb-0">Виконавець *</span>
          </div>

          <employee-autocomplete
            v-model="resolution.executor.userId"
            :error-messages="getErrorMessages(v$.executor.userId)"
            hide-details
            @blur="v$.executor.userId.$touch()">
          </employee-autocomplete>
        </div>

        <div>
          <div class="d-flex align-center ga-2 mb-2">
            <v-icon icon="mdi-clipboard-list-outline" size="x-small"></v-icon>
            <span class="label mb-0">Тип завдання</span>
          </div>
          <b-select
            v-model="resolution.executor.type"
            :items="executorTypeItems"
            hide-details
            item-title="name"
            item-value="id"
            placeholder="Оберіть тип"></b-select>
        </div>

        <div>
          <div class="d-flex align-center ga-2 mb-2">
            <v-icon icon="mdi-note-text-outline" size="x-small"></v-icon>
            <span class="label mb-0">Примітка</span>
          </div>
          <b-textarea
            v-model="resolution.executor.note"
            hide-details
            placeholder="Окрема примітка для виконавця"
            rows="2"></b-textarea>
        </div>

        <div>
          <div class="d-flex align-center ga-2 mb-2">
            <v-icon icon="mdi-calendar-blank-outline" size="x-small"></v-icon>
            <span class="label mb-0">Термін виконання *</span>
          </div>
          <div class="d-flex flex-wrap ga-1 mb-3">
            <v-btn
              v-for="preset in deadlinePresets"
              :key="preset.value"
              :active="resolution.dueDate === preset.value"
              border
              color="white"
              size="small"
              @click="setDueDate(preset.value)">
              <span class="font-weight-light font-size-12">
                {{ preset.label }}
              </span>
              <span class="text-disabled font-size-12 font-weight-light ml-1">
                {{ preset.date }}
              </span>
            </v-btn>
          </div>
          <b-input-date
            v-model="resolution.dueDate as string"
            :error-messages="getErrorMessages(v$.dueDate)"
            hide-details
            placeholder="дд.мм.рррр"
            @blur="v$.dueDate.$touch()"></b-input-date>
        </div>
      </div>
    </dialog-template>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import moment from 'moment'
import DialogTemplate from '@/dialog/DialogTemplate.vue'
import EmployeeAutocomplete from '@/components/inputs/employee-autocomplete/EmployeeAutocomplete.vue'
import { BInput, BInputDate, BSelect, BTextarea } from 'best-modules/components'
import { required, useVuelidate } from '@/plugins'
import { getErrorMessages } from '@/utils/helpers'
import { useDocument } from '@/pages/document/single/document'
import type {
  DocumentResolutionCredentials,
  DocumentResolutionExecutorType,
} from '@/utils/types/document'
import { ISODate } from 'best-modules/utils/customDate'

defineOptions({
  name: 'CreateResolution',
})

withDefaults(
  defineProps<{
    disabled?: boolean
  }>(),
  {
    disabled: false,
  }
)

const emit = defineEmits<{
  (_name: 'created'): void
}>()

const { myReview, createResolution } = useDocument()

const isOpen = ref<boolean>(false)

const resolution = reactive<DocumentResolutionCredentials>({
  text: null,
  comment: null,
  dueDate: null,
  executor: {
    userId: null,
    type: 'execution',
    note: null,
  },
})

const rules = {
  text: { required },
  dueDate: { required },
  executor: {
    userId: { required },
    type: { required },
  },
}
const v$ = useVuelidate(rules, resolution)

const executorTypeItems: {
  id: DocumentResolutionExecutorType
  name: string
}[] = [
  { id: 'execution', name: 'Виконання' },
  { id: 'familiarization', name: 'Ознайомлення' },
]

const deadlinePresets = computed(() => {
  const getPreset = (days: number, label: string) => {
    const date = moment().add(days, 'days')

    return {
      label,
      date: date.format('DD.MM'),
      value: date.format('YYYY-MM-DD') as ISODate,
    }
  }

  return [
    getPreset(0, 'Сьогодні'),
    getPreset(1, 'Завтра'),
    getPreset(3, '+3 дні'),
    getPreset(7, 'Тиждень'),
    getPreset(14, '+14 днів'),
  ]
})

const textLength = computed(() => resolution.text?.length || 0)
const commentLength = computed(() => resolution.comment?.length || 0)

const setDueDate = (date: ISODate) => {
  resolution.dueDate = date
  v$.value.dueDate.$touch()
}

const resetForm = () => {
  resolution.text = null
  resolution.comment = null
  resolution.dueDate = null
  resolution.executor.userId = null
  resolution.executor.type = 'execution'
  resolution.executor.note = null
  v$.value.$reset()
}

const submit = async () => {
  v$.value.$validate()

  if (v$.value.$invalid || !myReview.value?.id) return

  await createResolution(myReview.value.id, resolution)
  emit('created')
  isOpen.value = false
  resetForm()
}

watch(isOpen, value => {
  if (!value) {
    resetForm()
  }
})
</script>
